import {
  createNutritionLogSession,
  type NutritionLogSession,
} from '@calorie-tracker/ai';

export const deprecatedInitialLogAssistantText =
  'Tell me what you ate, or attach a meal photo, label, or voice note. I will draft reviewable entries here before anything is saved.';

export function createInitialLogSession(): NutritionLogSession {
  return createNutritionLogSession();
}

export function readSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

const preferredVoiceRecordingMimeTypes = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'video/mp4',
] as const;

export function getSupportedVoiceRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined;
  }

  return preferredVoiceRecordingMimeTypes.find((mimeType) =>
    MediaRecorder.isTypeSupported(mimeType),
  );
}

export function getAudioFileExtension(contentType: string): string {
  const normalizedContentType = contentType.split(';', 1)[0]?.trim().toLowerCase();

  switch (normalizedContentType) {
    case 'audio/mp4':
    case 'audio/x-m4a':
    case 'video/mp4':
      return 'm4a';
    case 'audio/mpeg':
      return 'mp3';
    case 'audio/ogg':
    case 'audio/opus':
      return 'ogg';
    case 'audio/wav':
    case 'audio/x-wav':
      return 'wav';
    case 'audio/webm':
    case 'video/webm':
      return 'webm';
    default:
      return 'webm';
  }
}

export function createVoiceNoteFileName(now: Date, contentType = 'audio/webm'): string {
  const extension = getAudioFileExtension(contentType);

  return `voice-log-${now.toISOString().replace(/[:.]/g, '-')}.${extension}`;
}

export function readBlobAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => {
      reject(reader.error ?? new Error('Unable to read the recorded audio clip.'));
    };
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Unable to encode the recorded audio clip.'));
        return;
      }

      const [, base64Value = ''] = reader.result.split(',', 2);
      resolve(base64Value);
    };
    reader.readAsDataURL(blob);
  });
}

export function createLogImageUploadErrorMessage(error: unknown): string {
  if (error instanceof Error && /8 MB/.test(error.message)) {
    return error.message;
  }

  if (error instanceof Error && /Only image uploads/.test(error.message)) {
    return 'Choose a meal photo or food label image to attach here.';
  }

  if (error instanceof Error && error.message.length > 0) {
    return `Image upload failed: ${error.message}`;
  }

  return 'Image upload failed. Please try again.';
}

export async function uploadFileToPresignedUrl(input: {
  contentType: string;
  file: File;
  headers: Record<string, string>;
  method: 'PUT';
  uploadUrl: string;
}) {
  const response = await fetch(input.uploadUrl, {
    method: input.method,
    headers: {
      ...input.headers,
      'Content-Type': input.contentType,
    },
    body: input.file,
  });

  if (!response.ok) {
    throw new Error(`Storage upload failed with status ${response.status}.`);
  }
}
